/**
 * The session generation: one monotonic counter shared by every store that
 * caches per-identity data.
 *
 * Sign-out, an account switch, a workspace switch and a /dev scenario reseed all
 * END the current session. Remounting the React subtree (LiveRoot keys
 * ConsoleRoutes by uid) re-runs the screens' loaders, but it CANNOT cancel a
 * zustand action that is already awaiting a network call — the action resumes
 * afterwards and writes into whatever store exists then. So every store mutation
 * that happens after an `await` must first ask whether it still belongs to the
 * session it started in. Otherwise the previous identity's response lands in the
 * next one's store: one tenant's rows under another's session at best, and at
 * worst the one-time plaintext secret from `createKey` rendered to whoever is
 * signed in when it finally resolves.
 *
 * Usage:
 *
 *     const alive = sessionGuard();
 *     const value = await getDataSource().something();
 *     if (!alive()) return;   // session ended mid-flight — drop the result
 *     set({ value });
 *
 * The counter only invalidates; it never clears state. Each store's reset still
 * clears its own slice, and a loader that bails as stale must not strand its
 * loading flag — see the per-request tickets in consoleStore/workspaceStore,
 * which decide whether a bailing request is still the newest one for its slice.
 */
let generation = 0;

/** Ends the current session, invalidating every in-flight store mutation. */
export function endSession(): void {
  generation += 1;
}

/**
 * Captures the current session. The returned predicate stays true until the
 * session ends, and false forever after.
 */
export function sessionGuard(): () => boolean {
  const started = generation;
  return () => started === generation;
}
